import type { TypographyVariantsOptions } from '@mui/material/styles';
import type { CSSProperties } from 'react';

/**
 * Enterprise Portal typography scale.
 * Product Menu Management Figma is the reference for hierarchy and rhythm.
 */
export const fontFamily =
  '"Inter", "Roboto", "Helvetica", "Arial", sans-serif';

export const fontFamilyMono =
  '"Roboto Mono", "Consolas", "Courier New", monospace';

export const typography: TypographyVariantsOptions = {
  fontFamily,
  fontWeightLight: 400,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,

  // Display — marketing / hero (rare)
  h1: {
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1.25,
    letterSpacing: '-0.02em',
  },

  // Page title — primary screen headings
  h2: {
    fontSize: '1.875rem',
    fontWeight: 700,
    lineHeight: 1.27,
    letterSpacing: '-0.02em',
  },

  // Page title (compact) / large section
  h3: {
    fontSize: '1.5rem',
    fontWeight: 700,
    lineHeight: 1.33,
    letterSpacing: '-0.015em',
  },

  // Workspace / panel title
  h4: {
    fontSize: '1.25rem',
    fontWeight: 700,
    lineHeight: 1.4,
    letterSpacing: '-0.01em',
  },

  // Section title
  h5: {
    fontSize: '1.125rem',
    fontWeight: 600,
    lineHeight: 1.44,
    letterSpacing: '-0.005em',
  },

  // Card title
  h6: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.5,
  },

  // Emphasized body — table primary cells, nav active
  subtitle1: {
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.43,
  },

  // Navigation items, buttons (secondary weight)
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.43,
  },

  // Default body — inputs, labels content
  body1: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.5,
  },

  // Secondary body — descriptions, metadata
  body2: {
    fontSize: '0.8125rem',
    fontWeight: 400,
    lineHeight: 1.5,
  },

  // Breadcrumbs, timestamps, footer
  caption: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.33,
  },

  // Panel / sidebar section headings (SELECT APPLICATION, NODE PROPERTIES)
  overline: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    lineHeight: 1.5,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },

  button: {
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.75,
    textTransform: 'none',
  },

  // Custom variants
  mono: {
    fontFamily: fontFamilyMono,
    fontSize: '0.8125rem',
    fontWeight: 400,
    lineHeight: 1.5,
  },

  navSection: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    lineHeight: 1.5,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },

  tableHeader: {
    fontSize: '0.75rem',
    fontWeight: 700,
    lineHeight: 1.33,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },

  chip: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    lineHeight: 1,
  },

  helper: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.33,
  },
};

declare module '@mui/material/styles' {
  interface TypographyVariants {
    mono: CSSProperties;
    navSection: CSSProperties;
    tableHeader: CSSProperties;
    chip: CSSProperties;
    helper: CSSProperties;
  }

  interface TypographyVariantsOptions {
    mono?: CSSProperties;
    navSection?: CSSProperties;
    tableHeader?: CSSProperties;
    chip?: CSSProperties;
    helper?: CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    mono: true;
    navSection: true;
    tableHeader: true;
    chip: true;
    helper: true;
  }
}
